import { useEffect, useState  } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCouponsByOffer, updateOfferCouponStatus } from "../../apiService/loyalty.service";
import useLoyaltyOffers from "./hooks/useLoyaltyOffers";
import Layout from "../../components/Layout/Layout";

const normalizeCouponFromApi = (coupon, index) => ({
  ...coupon,
  id: coupon?.id ?? coupon?._id ?? coupon?.coupon_id ?? `coupon-${index}`,
  code: coupon?.coupon_code ?? coupon?.code ?? coupon?.couponCode ?? `COUPON-${index + 1}`,
  type: coupon?.coupon_type ?? coupon?.type ?? "—",
  offer_id: coupon?.offer_id ?? coupon?.offerId ?? null,
  is_redeemed: Boolean(coupon?.is_redeemed ?? coupon?.redeemed ?? false),
  status: typeof coupon?.status === "string" ? coupon.status.toLowerCase() : coupon?.status ?? "inactive",
  created_at: coupon?.created_at ?? coupon?.createdAt ?? null,
  updated_at: coupon?.updated_at ?? coupon?.updatedAt ?? null,
  deleted_at: coupon?.deleted_at ?? coupon?.deletedAt ?? null,
});

export default function OfferCoupons() {
  const navigate = useNavigate();
  const [offerTitle, setOfferTitle] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { brandId, offerId } = useParams();
  const { coupons, fetchCouponsByOffer, setCoupons } = useLoyaltyOffers();
  

  const handleCouponStatusToggle = async (coupon) => {
    if (!coupon?.id) return;

    const nextStatus = coupon.status === "active" ? "inactive" : "active";

    try {
      await updateOfferCouponStatus(coupon.id);
      setCoupons((prev) =>
        prev.map((item) =>
          item.id === coupon.id ? { ...item, status: nextStatus } : item
        )
      );
    } catch (error) {
      console.error("Failed to update coupon status:", error);
    }
  };

  useEffect(() => {
    if (!offerId) return;

    let cancelled = false;

    const loadCoupons = async () => {
      try {
        const response = await getCouponsByOffer(offerId, currentPage);
        if (cancelled) return;

        setOfferTitle(response?.offer?.offer_title_en || "");
        setCurrentPage(Number(response?.currentPage || currentPage));
        setTotalPages(Number(response?.totalPages || 1));

        const couponList = Array.isArray(response?.dataList) ? response.dataList : [];

        const normalizedCoupons = couponList.map((coupon, index) =>
          normalizeCouponFromApi(coupon, index)
        );

        fetchCouponsByOffer(offerId, normalizedCoupons);
      } catch (error) {
        if (!cancelled) {
          fetchCouponsByOffer(offerId, []);
        }
      }
    };

    loadCoupons();

    return () => {
      cancelled = true;
    };
  }, [currentPage, fetchCouponsByOffer, offerId]);

  const handlePageChange = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  };

  const offerName = offerTitle || `Offer ${offerId || ""}`.trim();

  return (
    <Layout>
      <main>
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Offer</p>
          <h1 className="text-3xl font-bold text-gray-900">{offerName}</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-white"
            onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers/${offerId}/coupons/add`, { state: { brandId, offerId } })}
          >
            Add Coupon
          </button>
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
            onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers`)}
          >
            Back to Offers
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {coupons.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {/* <th className="px-4 py-3 font-semibold">ID</th> */}
                  <th className="px-4 py-3 font-semibold">Offer ID</th>
                  <th className="px-4 py-3 font-semibold">Coupon Code</th>
                  <th className="px-4 py-3 font-semibold">Coupon Type</th>
                  <th className="px-4 py-3 font-semibold">Redeemed</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created At</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="align-middle text-gray-700">
                    {/* <td className="px-4 py-3 font-medium text-gray-800">{coupon.id ?? "—"}</td> */}
                    <td className="px-4 py-3 text-gray-600">{coupon.offer_id ?? "—"}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{coupon.code ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{coupon.type ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          coupon.is_redeemed ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {coupon.is_redeemed ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={coupon.status === "active"}
                        aria-label={`Toggle coupon ${coupon.code ?? coupon.id} status`}
                        onClick={() => handleCouponStatusToggle(coupon)}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                          coupon.status === "active" ? "bg-cyan-500" : "bg-gray-200"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            coupon.status === "active" ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {coupon.created_at ? new Date(coupon.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700"
                        onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers/${offerId}/coupons/edit/${coupon.id}`, { state: { coupon } })}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-slate-50 p-10 text-center text-gray-500">
            No coupons found for this offer.
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>

          <span className="text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            type="button"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || totalPages <= 1}
          >
            Next
          </button>
        </div>
      </div>
    </div>
    </main>
    </Layout>
  );
}
